// biome-ignore lint/style/useNodejsImportProtocol: we use the events module, but biome can't detect it in a monorepo yet
import EventEmitter from 'events';

import {
    applyRevisionComputations,
    type ContentComputer,
    createRevisionChange,
    getYRevision,
    normalizeRevision,
    type Revision,
    type RevisionChange,
    type RevisionComputations,
    type YRevision,
    yToRevision,
} from '@gitbook/hive-core';
import { deepFreeze } from '@gitbook/util-immutable';

import type { HiveDataSyncBrowserCache } from './HiveDataSyncBrowserCache';
import {
    HiveDataSyncClient,
    HiveDataSyncUpdateOrigin,
    type HiveGlobalPresence,
} from './HiveDataSyncClient';
import type { HiveDataSyncManager } from './HiveDataSyncManager';

export type HiveBranchPresence = HiveGlobalPresence & {
    pageId?: string;
};

export interface HiveWorkingBranchOptions {
    /**
     * Is the datasync client opened in read-only mode
     */
    readOnly?: boolean;

    /**
     * A cache to use with the datasync.
     */
    cache?: HiveDataSyncBrowserCache;
}

export interface HiveWorkingBranchState {
    /**
     * Revision with all computations applied.
     */
    revision: Revision;

    /**
     * Revision before computations.
     */
    sourceRevision: Revision;

    /**
     * Computations applied to the revision.
     */
    computations: RevisionComputations;

    /**
     * True if the whole state has been computed.
     */
    isComputed: boolean;
}

/**
 * A live branch that is updated when the revision changes.
 */
export class HiveWorkingBranch {
    public readonly contentId: string;
    public readonly branchId: string;
    public readonly dataSync: HiveDataSyncClient<HiveBranchPresence>;

    private readonly manager: HiveDataSyncManager;
    private readonly _change: RevisionChange<YRevision>;

    private events = new EventEmitter();

    private readonly computer: ContentComputer;
    private cancelComputation: (() => void) | null = null;

    #state: HiveWorkingBranchState | null;

    constructor(
        manager: HiveDataSyncManager,
        guid: string,
        params: { contentId: string; branchId: string },
        options: HiveWorkingBranchOptions = {}
    ) {
        this.manager = manager;
        this.contentId = params.contentId;
        this.branchId = params.branchId;

        this.computer = manager.client.getContentComputer(this.contentId);

        this.dataSync = new HiveDataSyncClient(manager.client, {
            guid,
            contentId: this.contentId,
            path: '/branches/:branchId/datasync',
            pathParams: {
                branchId: this.branchId,
            },
            readOnly: options.readOnly,
            cache: options.cache,
        });
        this._change = createRevisionChange(getYRevision(this.dataSync.doc));

        this.dataSync.listen('update', () => {
            this.dataSync.doc.transact(() => {
                normalizeRevision(getYRevision(this.dataSync.doc));
            }, HiveDataSyncUpdateOrigin.LOCAL);

            this.updateSourceRevision();
        });
    }

    /**
     * Get the current state of the branch, before a potential running `.change()` operation.
     */
    get state() {
        if (!this.#state) {
            throw new Error('.state called before the constructor');
        }

        return this.#state;
    }

    /**
     * Get the state of computations of the branch.
     */
    get isComputed() {
        return this.state.isComputed;
    }

    /**
     * Current revision of the branch, before a potential running `.change()` operation.
     * This is the revision after computations have been applied.
     */
    get revision(): Revision {
        return this.state.revision;
    }

    /**
     * Current source revision of the branch, before a potential running `.change()` operation.
     * This is the revision before computations have been applied.
     */
    get sourceRevision(): Revision {
        return this.state.sourceRevision;
    }

    /**
     * Get the list of presences in the branch.
     */
    get presences() {
        return this.dataSync.presences;
    }

    /**
     * Prevent the manager from closing this sync before the cleanup callback is called.
     */
    public keepAlive() {
        return this.manager.keepAlive(this);
    }

    /**
     * Initialize the branch by reading its current revision and start listening
     * to changes.
     */
    public open(): void | Promise<void> {
        return this.dataSync.open(
            this.manager.clientId
                ? {
                      clientId: this.manager.clientId,
                  }
                : {}
        );
    }

    /**
     * Close the connection to the branch and garbage collect it.
     */
    public close() {
        this.manager.close(this);
    }

    /**
     * Destroy the connection. It cannot be re-opened later and needs to be recreated.
     */
    public destroy() {
        this.dataSync.destroy();
    }

    /**
     * Wait for all changes to be synced.
     */
    public waitForSync() {
        return this.dataSync.waitForSync();
    }

    /**
     * Add a callback to be called when the revision changes.
     */
    public listen(event: 'update' | 'presences' | 'operation', callback: () => void): () => void {
        if (event === 'update') {
            this.events.addListener(event, callback);
            return () => {
                this.events.removeListener(event, callback);
            };
        }

        return this.dataSync.listen(event, callback);
    }

    /**
     * Dispatch a presence update to indicate that a page has been opened.
     */
    public openPage(pageId: string) {
        const current = this.dataSync.presence;
        this.manager.touch(this);

        if (current) {
            this.dataSync.setPresence({
                data: {
                    ...current.data,
                    pageId,
                },
            });
        }
    }

    /**
     * Apply an update to the revision, without waiting for the change to be acknowledged by the server.
     */
    public syncChange<Return>(fn: (change: RevisionChange<YRevision>) => Return): Return {
        this.manager.touch(this);

        const result = this.dataSync.transact(() => {
            return fn(this._change);
        });

        return result;
    }

    /**
     * Apply an update to the revision.
     * It returns a promise to indicate when the change has been acknowledged by the server.
     */
    public async change<Return>(
        fn: (change: RevisionChange<YRevision>) => Return
    ): Promise<Return> {
        if (this.dataSync.readOnly) {
            throw new Error(`can't apply change when dataSync is in read-only mode`);
        }

        const result = this.syncChange(fn);
        await this.waitForSync();

        return result;
    }

    /**
     * Wait for the revision to be computed.
     */
    public waitForComputed() {
        if (this.state.isComputed) {
            return;
        }

        return new Promise<void>((resolve) => {
            const callback = () => {
                if (this.state.isComputed) {
                    this.events.off('update', callback);
                    resolve();
                }
            };

            this.events.on('update', callback);
        });
    }

    /**
     * Prepare the revision to display.
     */
    private updateSourceRevision() {
        const sourceRevision = yToRevision(getYRevision(this.dataSync.doc));

        // Cancel previous computation
        this.cancelComputation?.();
        this.cancelComputation = null;

        if (!this.manager.client.hasContentComputer()) {
            this.updateState(createNonComputedState(sourceRevision));
            return;
        }

        // Update the state with the result of the computation
        const updateWithResult = (result: RevisionComputations) => {
            const applyResult = applyRevisionComputations({
                revision: sourceRevision,
                computations: result,
            });

            this.updateState({
                sourceRevision,
                revision: applyResult.result,
                computations: result,
                isComputed: applyResult.complete,
            });
        };

        const stream = this.computer.computeRevision({
            revision: sourceRevision,
            onUpdate: (result) => {
                if (this.#state?.sourceRevision !== sourceRevision) {
                    // The source revision has changed since the computation started
                    return;
                }

                updateWithResult(result);
            },
        });
        this.cancelComputation = stream.cancel;

        // Initialize with the initial state
        updateWithResult(stream.result);
    }

    private updateState(state: HiveWorkingBranchState) {
        // During development, we freeze it to ensure that it's not mutated
        // as only the Yjs one should be mutated.
        this.#state = developmentFreeze(state);
        this.events.emit('update');
    }
}

/**
 * Create a state for a branch without any computations applied.
 * Accessing .revision should throw an error.
 */
function createNonComputedState(sourceRevision: Revision): HiveWorkingBranchState {
    const rawState: HiveWorkingBranchState = {
        ...developmentFreeze({
            sourceRevision,
            computations: { pages: {} },
            isComputed: true,
        }),
        get revision(): Revision {
            throw new Error(
                '.revision should not be used to access the revision of a branch when no computer is configured on the client. Use .sourceRevision instead.'
            );
        },
    };
    return process.env.NODE_ENV !== 'production' ? Object.freeze(rawState) : rawState;
}

function developmentFreeze<T>(state: T): T {
    return process.env.NODE_ENV !== 'production' && !Object.isFrozen(state)
        ? // @ts-expect-error
          deepFreeze(state)
        : state;
}
